import React, { useState, useEffect } from 'react';
import '../styles/config-category.css';
import { useAuth } from '../context/AuthContext';
import { authenticatedFetch } from '../context/apiClient';

const HallucinationCategories = () => {
    const [categories, setCategories] = useState([]);
    const [selectedCategoryName, setSelectedCategoryName] = useState('');
    const [categoryName, setCategoryName] = useState('');
    const [severityLevel, setSeverityLevel] = useState('');
    const [description, setDescription] = useState('');
    const [isNewMode, setIsNewMode] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const { user, logout } = useAuth();

    useEffect(() => {
        const fetchCategories = async () => {
            if (!user?.token) return;
            try {
                const response = await authenticatedFetch('/api/hallucination-config', {}, user.token, logout);
                if (!response.ok) throw new Error('Failed to fetch hallucination categories');
                const config = await response.json();
                if (config && config.length > 0) {
                    setCategories(config);
                    setSelectedCategoryName(config[0].name);
                }
            } catch (e) {
                console.error('Fetch hallucination categories error:', e);
            }
        };
        fetchCategories();
    }, [user, logout]);

    useEffect(() => {
        if (isNewMode) return;
        const category = categories.find(c => c.name === selectedCategoryName);
        if (category) {
            setCategoryName(category.name || '');
            setSeverityLevel(category.type || '');
            setDescription(category.description || '');
        }
    }, [selectedCategoryName, categories, isNewMode]);

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
    };

    const handleAddNew = () => {
        setIsNewMode(true);
        setSelectedCategoryName('');
        setCategoryName('');
        setSeverityLevel('');
        setDescription('');
        setSelectedFile(null);
        const fileInput = document.getElementById('file-upload-input');
        if (fileInput) fileInput.value = '';
    };

    const handleCancel = () => {
        setIsNewMode(false);
        if (categories.length > 0) {
            setSelectedCategoryName(categories[0].name);
        }
    };

    const handleDownloadExample = () => {
        const csvContent = "query,expected_response\nWhat is the capital of France?,Paris\nWho wrote Romeo and Juliet?,William Shakespeare\nWhat is 2 + 2?,4";
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'example_hallucination_tests.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDelete = async () => {
        if (!selectedCategoryName) {
            alert('Please select a category to delete.');
            return;
        }

        if (window.confirm(`Are you sure you want to delete the category "${selectedCategoryName}"?`)) {
            try {
                const response = await authenticatedFetch('/api/hallucination-categories', {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name: selectedCategoryName }),
                }, user.token, logout);

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Failed to delete category');
                }

                alert('Category deleted successfully!');

                // Refresh categories list after deletion
                const fetchResponse = await authenticatedFetch('/api/hallucination-config', {}, user.token, logout);
                const config = await fetchResponse.json();
                setCategories(config.categories || []);
                setSelectedCategoryName(config.categories.length > 0 ? config.categories[0].name : '');
            } catch (error) {
                console.error('Delete category error:', error);
                alert(error.message);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isNewMode) {
            const formData = new FormData();
            if (selectedFile) {
                formData.append("file", selectedFile);
            }
            formData.append("name", categoryName);
            formData.append("description", description);
            formData.append("type", severityLevel);

            try {
                const response = await authenticatedFetch('/api/hallucination-categories', {
                    method: 'POST',
                    body: formData,
                }, user.token, logout);

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Failed to add new category, please provide all info include csv file.');
                }

                alert('Category added successfully!');
                // Refresh categories list and exit new mode
                const fetchResponse = await authenticatedFetch('/api/hallucination-config', {}, user.token, logout);
                const config = await fetchResponse.json();
                setCategories(config.categories || []);
                setSelectedCategoryName(categoryName);
                setIsNewMode(false);
            } catch (error) {
                console.error('Add category error:', error);
                alert(error.message);
            }
        } else {
            // Logic for updating an existing category will go here
            console.log("Updating:", { categoryName, severityLevel, description });
        }
    };

    return (
        <div className="config-category-page">
            <div className="container">
                <h1 className="page-title">Hallucination Category Management</h1>

                <div className="layout-wrapper">
                    {/* Left Panel - Categories List */}
                    <div className="categories-list-panel">
                        <div className="list-header">
                            <h2>Categories</h2>
                            <button type="button" className="btn-add-new" onClick={handleAddNew}>
                                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                                </svg>
                                Add New
                            </button>
                        </div>

                        <div className="categories-list">
                            {isNewMode && (
                                <div className="category-card active new-category">
                                    <div className="category-badge new">
                                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                                        </svg>
                                    </div>
                                    <div className="category-info">
                                        <h3>New Category</h3>
                                        <p>Fill in the details</p>
                                    </div>
                                </div>
                            )}
                            {categories.map((category) => (
                                <div
                                    key={category.name}
                                    className={`category-card ${selectedCategoryName === category.name && !isNewMode ? 'active' : ''}`}
                                    onClick={() => {
                                        setSelectedCategoryName(category.name);
                                        setIsNewMode(false);
                                    }}
                                >
                                    <div className={`category-badge ${category.type?.toLowerCase() || 'default'}`}>
                                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                                        </svg>
                                    </div>
                                    <div className="category-info">
                                        <h3>{category.name}</h3>
                                        <p className={`severity-label medium || 'default'}`}>
                                            {category.type || 'No Type'}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Panel - Category Details */}
                    <div className="category-details-panel">
                        {(selectedCategoryName || isNewMode) ? (
                            <form className="details-form" onSubmit={handleSubmit}>
                                <div className="details-header">
                                    <h2>{isNewMode ? 'New Category' : 'Category Details'}</h2>
                                    {isNewMode && (
                                        <button type="button" className="btn-close" onClick={handleCancel}>
                                            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                                            </svg>
                                        </button>
                                    )}
                                </div>

                                <div className="form-content">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="category-name" className="form-label">Category Name</label>
                                            <input
                                                id="category-name"
                                                type="text"
                                                value={categoryName}
                                                onChange={e => setCategoryName(e.target.value)}
                                                disabled={!isNewMode && categories.length > 0}
                                                placeholder="Enter category name"
                                                required
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="severity-level" className="form-label">Type</label>
                                            <select
                                                id="severity-level"
                                                value={severityLevel}
                                                onChange={e => setSeverityLevel(e.target.value)}
                                            >
                                                <option value="">Select</option>
                                                <option value="Adversarial">Adversarial</option>
                                                <option value="Non-Adversarial">Non-Adversarial</option>
                                               
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="description" className="form-label">Description</label>
                                        <textarea
                                            id="description"
                                            value={description}
                                            onChange={e => setDescription(e.target.value)}
                                            placeholder="Describe this category and its test cases"
                                            rows="3"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Upload Test Cases (CSV)</label>
                                        <div className="file-upload">
                                            <input
                                                type="file"
                                                id="file-upload-input"
                                                onChange={handleFileChange}
                                                accept=".csv"
                                                style={{ display: 'none' }}
                                            />
                                            <label htmlFor="file-upload-input" className="file-upload-label">
                                                <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                </svg>
                                                <div className="upload-text">
                                                    {selectedFile ? selectedFile.name : <><strong>Click to upload</strong> or drag and drop</>}
                                                </div>
                                                <div className="upload-hint">
                                                    CSV file (max 5MB)
                                                </div>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="instructions">
                                        <div className="instructions-title">
                                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                            </svg>
                                            CSV Format Requirements
                                        </div>
                                        <ul>
                                            <li>Two columns: question and best_answer</li>
                                            <li>Header row required</li>
                                            <li>UTF-8 encoding</li>
                                            <li>Maximum 500 test cases per file</li>
                                        </ul>
                                        <button type="button" className="btn-download-example" onClick={handleDownloadExample}>
                                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                            </svg>
                                            Download Example CSV
                                        </button>
                                    </div>
                                </div>

                                <div className="form-actions">
                                    {!isNewMode && (
                                        <button type="button" className="btn btn-danger" onClick={handleDelete}>
                                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                            </svg>
                                            Delete
                                        </button>
                                    )}
                                    {isNewMode && (
                                        <button type="submit" className="btn btn-primary">
                                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                                            </svg>
                                            Create Category
                                        </button>
                                    )}
                                </div>
                            </form>
                        ) : (
                            <div className="empty-state">
                                <svg width="64" height="64" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                                </svg>
                                <h3>No Category Selected</h3>
                                <p>Select a category from the list or create a new one</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HallucinationCategories;
